import { useEffect, useRef } from 'react';
import { plural, t } from '@lingui/macro';
import get from 'lodash-es/get';
import styled from 'styled-components';

import { Box, Text } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import useTheme from '@rover/kibble/themes/useTheme';
import Advanced from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Advanced';
import PriceFilter from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/PriceFilter';
import StarSitterFilter from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/StarSitterFilter';
import PetCounter from '@rover/react-lib/src/pages/search/SearchPage/components/PetCounter';
import {
  advancedFiltersCounter,
  getServiceType,
  hasAdvancedFilters,
  shouldShowControls,
} from '@rover/react-lib/src/pages/search/SearchPage/utilities';
import { lockPriceToggle } from '@rover/react-lib/src/pages/search/SearchPage/utilities/shouldShowControls';
import { useSimplifiedFilters } from '@rover/react-lib/src/pages/search/SearchPage/utilities/useSimplifiedFilters';
import type { ApiFrontendConfigurationRetrieve200SettingsDogSizeBuckets } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendConfigurationRetrieve200SettingsDogSizeBuckets';
import { useGateValue } from '@rover/rsdk/src/modules/Statsig';
import useAvailablePets from '@rover/shared/js/hooks/useAvailablePets';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';
import useFlag, { FLAGS } from '@rover/shared/js/hooks/useFlag';
import useIsBHUserShim from '@rover/shared/js/hooks/useIsBHUserShim';
import Pets from '@rover/shared/js/search/components/Pets';
import PetSize from '@rover/shared/js/search/components/PetSize';
import getIsCatOnlyPetType from '@rover/shared/js/search/utilities/getIsCatOnlyPetType';
import type { SearchDerivedData } from '@rover/types/src/apiResponses/FetchSearchResponse';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import type { Pet } from '@rover/types/src/Pet';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import type { ServiceType } from '@rover/types/src/ServiceType';
import { ServiceTypeSlug } from '@rover/types/src/ServiceType';
import { isCatOnlyAvailablePetTypesBasedOnPetTypes } from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';
import {
  getIsPriceTransparency,
  getIsPriceTransparencyFull,
} from '@rover/utilities/priceTransparency';

import { FilterWrapper } from '../common';
import { FulltimeAvailability } from '../Dates';
import PriceToggle from '../PriceToggle';

export type Props = {
  getServiceTypeBySlug: (arg0: string) => ServiceType | null | undefined;
  countryCode?: string;
  currencyCode: string;
  formData: Partial<SearchFilters>;
  getPriceUnit: (arg0: string) => string;
  hasPets: boolean;
  language: string;
  onChange: (arg0: Partial<SearchFilters>) => void;
  petOptions: Pet[];
  petSizeBuckets: ApiFrontendConfigurationRetrieve200SettingsDogSizeBuckets;
  searchFilters: SearchFilters;
  serviceTypeFilterOptions: SearchFiltersOptions;
  weightUnit: string;
  priceFilterMaxValue: number;
  priceFilterMinValue: number;
  queryDerivedData: SearchDerivedData;
  isShowingPriceInCurrency: boolean;
  toggleIsShowingPriceInCurrency: (args0: boolean) => void;
};

const AdvancedWrapper = styled(FilterWrapper)<{ $showBackground?: boolean }>`
  margin-left: ${({ theme }) => theme.space['-4x']};
  margin-right: ${({ theme }) => theme.space['-4x']};
  margin-bottom: 0;
  padding: ${({ theme }) => theme.space['4x']};
  padding-bottom: ${({ theme }) => theme.space['16x']};
  ${({ $showBackground, theme }) =>
    $showBackground ? `background-color: ${theme.background.secondary};` : ''}

  ${MQ.XS_DOWN.toString()} {
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const AdvancedFiltersCollection = ({
  countryCode,
  currencyCode,
  formData,
  getPriceUnit,
  hasPets,
  language,
  onChange,
  petOptions,
  petSizeBuckets,
  searchFilters,
  serviceTypeFilterOptions,
  weightUnit,
  priceFilterMaxValue,
  priceFilterMinValue,
  getServiceTypeBySlug,
  queryDerivedData,
  isShowingPriceInCurrency,
  toggleIsShowingPriceInCurrency,
}: Props): JSX.Element => {
  const brandAvailablePets = useBrandSpecificPets();
  const isCatOnlyBrand = isCatOnlyAvailablePetTypesBasedOnPetTypes(brandAvailablePets);
  const serviceType = getServiceTypeBySlug(searchFilters.serviceType);
  const { showStarSitterFilter } = queryDerivedData;
  const { showLowBookingFilter } = queryDerivedData;
  const { starSitterLink } = queryDerivedData;
  const isIncludingFees = serviceTypeFilterOptions.minprice?.fullprice === true;
  const { isStaffOrImpersonating } = useCurrentUserShim();

  const isBHUser = useIsBHUserShim();
  const isBhRepeatBookingFlagEnabled = useGateValue('rollout_bright_horizons_recurring');
  const isPriceTransparencyFull = getIsPriceTransparencyFull(
    queryDerivedData.priceTransparencyLevel
  );
  const isPriceTransparency = getIsPriceTransparency(queryDerivedData.priceTransparencyLevel);

  const { colors } = useTheme();

  const Line = styled.div`
    width: 100%;
    border-bottom: 1px solid;
    border-color: ${colors.border.primary};
  `;

  const {
    showPetSize,
    showPets,
    showPrice,
    showPetType,
    showStarSitterOnlyFilter,
    showFulltimeAvailability,
  } = shouldShowControls({
    hasPets,
    isCatOnly: getIsCatOnlyPetType(formData.petType || []) || isCatOnlyBrand,
    serviceOptions: serviceTypeFilterOptions,
    serviceTypeSlug: searchFilters.serviceType,
    showStarSitterFilter,
    showLowBookingFilter,
    isIncludingFees,
    isPriceTransparencyFull,
    isStaffOrImpersonating,
    searchFilters,
    isBHUser,
  });

  const isFiltersM2 = useFlag(FLAGS.ROLLOUT_MOBILE_FILTERS_M2);

  const { isActive: isUseSimplifiedFiltersActive } = useSimplifiedFilters();
  const previousLockState = useRef<boolean>(
    lockPriceToggle(countryCode ?? '', formData, isBHUser, isBhRepeatBookingFlagEnabled)
  );

  useEffect(() => {
    if (lockPriceToggle(countryCode ?? '', formData, isBHUser, isBhRepeatBookingFlagEnabled)) {
      toggleIsShowingPriceInCurrency(true);
    }
  }, [countryCode, toggleIsShowingPriceInCurrency]); // eslint-disable-line react-hooks/exhaustive-deps

  const availablePets = useAvailablePets(searchFilters.serviceType);

  const handleOnChange = (arg0: Partial<SearchFilters>): void => {
    const updatedFormData = { ...formData, ...arg0 };
    const currentLockState = lockPriceToggle(
      countryCode ?? '',
      updatedFormData,
      isBHUser,
      isBhRepeatBookingFlagEnabled
    );
    onChange(arg0);
    if (!previousLockState.current && currentLockState) {
      toggleIsShowingPriceInCurrency(true);
    }

    previousLockState.current = currentLockState;
  };

  return (
    <>
      {!isUseSimplifiedFiltersActive && showPetType && (
        <FilterWrapper>
          <PetCounter
            inputLabel={t`I'm looking for service for my:`}
            inputLabelMarginBottom="2x"
            inputLabelColor="secondary"
            dogCount={formData.dogCount}
            catCount={formData.catCount}
            puppyCount={formData.puppyCount}
            availablePets={availablePets}
            triggeredFrom="Search"
            onChange={({ petType, dogCount, catCount, puppyCount }) => {
              const localIsCatOnly = getIsCatOnlyPetType(petType);
              const updatedServiceType = getServiceType(
                localIsCatOnly,
                searchFilters.serviceType as ServiceTypeSlug,
                get(serviceType, 'suggestedPetType')
              );
              const args = {
                petType,
                catCare: localIsCatOnly,
                serviceType: updatedServiceType,
                dogCount,
                catCount,
                puppyCount,
              };
              return handleOnChange(args);
            }}
            hideHelperText
          />
        </FilterWrapper>
      )}
      {!isUseSimplifiedFiltersActive && showPets && (
        <FilterWrapper>
          <Pets
            label={plural(
              serviceTypeFilterOptions.pets ? serviceTypeFilterOptions.pets.choices.length : 0,
              {
                one: 'Your pet:',
                other: 'Your pets:',
              }
            )}
            petOptions={petOptions}
            searchFilters={formData}
            onChange={({ pet, petType }) => {
              const localIsCatOnly = getIsCatOnlyPetType(petType as []);
              const newServiceType = getServiceType(
                localIsCatOnly,
                searchFilters.serviceType as ServiceTypeSlug,
                get(serviceType, 'suggestedPetType')
              );
              return handleOnChange({
                pet,
                petType,
                catCare: localIsCatOnly,
                serviceType: newServiceType,
              });
            }}
          />
          {!formData?.pet?.length && (
            <Text size="100" textColor="tertiary" data-testid="helper-text">
              {t`Select at least one pet to ensure a more accurate search`}
            </Text>
          )}
        </FilterWrapper>
      )}
      {!isUseSimplifiedFiltersActive && showPetSize && (
        <FilterWrapper>
          <PetSize
            label={t`What size are your dogs (${weightUnit})?`}
            onChange={(sizeKey) => {
              handleOnChange({ [sizeKey]: !formData[sizeKey] });
            }}
            petSizeBuckets={petSizeBuckets}
            searchFilters={formData}
            weightUnit={weightUnit}
          />
        </FilterWrapper>
      )}
      {!!(showPrice && formData.serviceType && formData.minprice) && (
        <FilterWrapper>
          <Box
            pb={isUseSimplifiedFiltersActive ? '4x' : '0x'}
            pt={isUseSimplifiedFiltersActive ? '2x' : '0x'}
          >
            <PriceFilter
              currencyCode={currencyCode}
              priceUnit={getPriceUnit(formData.serviceType)}
              priceTransparencyLevel={queryDerivedData.priceTransparencyLevel}
              isStaffOrImpersonating={isStaffOrImpersonating}
              language={language}
              onChange={({ min, max }) => {
                handleOnChange({ minprice: min, maxprice: max });
              }}
              selectedRangeMin={formData.minprice}
              selectedRangeMax={formData.maxprice || priceFilterMaxValue}
              sliderMax={priceFilterMaxValue}
              sliderMin={priceFilterMinValue}
              isIncludingFees={isIncludingFees}
              showPriceInputs={isFiltersM2}
            />
          </Box>
        </FilterWrapper>
      )}
      {isUseSimplifiedFiltersActive && <Line />}
      {isUseSimplifiedFiltersActive && showStarSitterOnlyFilter && (
        <Box pb="6x" pt={isBHUser || isUseSimplifiedFiltersActive ? '6x' : '0x'}>
          <StarSitterFilter
            label={t`Only show star sitters`}
            onChange={(starSitter) => handleOnChange({ starSitter })}
            value={formData.starSitter || false}
            starSitterLink={starSitterLink}
          />
        </Box>
      )}
      {isUseSimplifiedFiltersActive && <Line />}
      {showFulltimeAvailability && isUseSimplifiedFiltersActive && (
        <>
          <Box py="6x">
            <FulltimeAvailability
              label={t`Sitter is home full-time`}
              searchFilters={searchFilters}
              onChange={(fulltimeAvailability) => handleOnChange({ fulltimeAvailability })}
            />
          </Box>
          <Line />
        </>
      )}
      {isBHUser && (
        <FilterWrapper>
          <PriceToggle
            unit={getPriceUnit(searchFilters.serviceType)}
            currencyCode={currencyCode}
            onChange={(toggle) => toggleIsShowingPriceInCurrency(toggle)}
            value={isShowingPriceInCurrency}
            source="search"
            disabled={lockPriceToggle('', formData, isBHUser, isBhRepeatBookingFlagEnabled)}
          />
        </FilterWrapper>
      )}
      {!isUseSimplifiedFiltersActive && showStarSitterOnlyFilter && (
        <Box pb="6x" pt={isBHUser ? '6x' : '0x'}>
          <StarSitterFilter
            label={t`Only show star sitters`}
            onChange={(starSitter) => handleOnChange({ starSitter })}
            value={formData.starSitter || false}
            starSitterLink={starSitterLink}
          />
        </Box>
      )}
      {hasAdvancedFilters(serviceTypeFilterOptions) && (
        <AdvancedWrapper $showBackground={!isFiltersM2}>
          <Advanced
            advancedFiltersCount={advancedFiltersCounter(formData, serviceTypeFilterOptions)}
            onChange={(filter) => handleOnChange(filter)}
            searchFiltersOptions={serviceTypeFilterOptions}
            searchFilters={formData}
            isPriceTransparency={isPriceTransparency}
            showLowBookingFilter={!!showLowBookingFilter}
            cardTreatment={queryDerivedData.cardTreatment}
          />
        </AdvancedWrapper>
      )}
    </>
  );
};

export default AdvancedFiltersCollection;
